import { useEffect, useCallback, useState } from 'react';
import { useLocation } from 'react-router';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { SplashScreen } from 'src/components/loading-screen';
// hooks and Redux
import { useDispatch, useSelector } from 'src/redux/store';
import { fetchData } from 'src/redux/slices/dataSlice';
import { selectData, selectDataLoading } from 'src/redux/selectors';
import { fetchForeignBeneficiaries, fetchLocalBeneficiaries, fetchTransfers } from 'src/redux/slices/transfersSlice';
import { fetchGuarantees, fetchProjects } from 'src/redux/slices/cautionSlice';
import { HOST_API } from 'src/config-global';
import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

const loginPaths: Record<string, string> = {
  jwt: paths.auth.jwt.login,
};

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const dispatch = useDispatch();

  const { authenticated, method } = useAuthContext();

  const [checked, setChecked] = useState(false);

  const data = useSelector(selectData);
  // const transfers = useSelector(selectTransfers);
  // const localBeneficiaries = useSelector(selectLocalBeneficiaries);
  // const foreignBeneficiaries = useSelector(selectForeignBeneficiaries);
  // const projects = useSelector(selectProjects);
  // const guarantees = useSelector(selectGuarantees);
  const loading = useSelector(selectDataLoading);
  const location = useLocation();

  const check = useCallback(() => {
    if (!authenticated) {
      const searchParams = new URLSearchParams({ returnTo: window.location.href }).toString();

      const loginPath = loginPaths[method];

      const href = `${loginPath}?${searchParams}`;

      router.replace(href);
    } else {
      setChecked(true);
    }
  }, [authenticated, method, router]);

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authenticated) {
      const fetchAllData = async () => {
        const accessToken = localStorage.getItem('lbankaAccessToken');
        const headers = {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        };

        try {
          const response = await fetch(`${HOST_API}/all-data`, { headers });
          if (response.ok) {
            const allData = await response.json();

            // Dispatch each thunk with the relevant portion of the data
            dispatch(fetchData(allData.initialData));
            dispatch(fetchTransfers(allData.transfers));
            dispatch(fetchLocalBeneficiaries(allData.localBeneficiaries));
            dispatch(fetchForeignBeneficiaries(allData.foreignBeneficiaries));
            dispatch(fetchProjects(allData.projects));
            dispatch(fetchGuarantees(allData.guarantees));
          } else {
            throw new Error('Failed to fetch all data');
          }
        } catch (error) {
          console.error('Error fetching all data:', error);
        }
      };

      fetchAllData();
    }
  }, [authenticated, dispatch]);

  useEffect(() => {
    if (authenticated && data && !(location.pathname === paths.lbanka.new_account)) {
      if (data.companies.length === 0) {
        router.replace(paths.lbanka.new_account);
      }
    }
  }, [authenticated, data, location.pathname, router]);

  if (!checked || loading) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
